import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { AttendingService } from '../../service/attending.service';
import { Router, ActivatedRoute } from '@angular/router';
import { Attending } from '../../model/attending';
import { Branch } from '../../../shared/model/branch';
import { Equipment } from '../../../shared/model/equipment';
import { Screening } from '../../model/screening';
import { Configuration } from '../../model/configuration';
import { Dispatch } from '../../model/dispatch';
import { Monitoring } from '../../model/monitoring';
import { Scheduling } from '../../model/scheduling';
import { AuthenticationService } from '../../../shared/service/authentication.service';
import { ToastService } from '../../../shared/service/toast.service';
import AttendingInfo from '../../model/attending-info';
import { Validation } from '../../model/validation';

@Component({
  selector: 'tx-attending',
  templateUrl: './attending.component.html',
  styleUrls: ['./attending.component.css']
})
export class AttendingComponent implements OnInit, OnDestroy {
  attendingId: number;
  creating = false;
  wasSaved = false;
  authenticatedUser: any;
  attending: Attending = new Attending();
  canShowAttending = false;

  SCREENING = 'SCREENING';
  CONFIGURATION = 'CONFIGURATION';
  DISPATCH = 'DISPATCH';
  SCHEDULING = 'SCHEDULING';
  MONITORING = 'MONITORING';
  VALIDATION = 'VALIDATION';

  isLoading = false;
  totalProcessingRequests = 0;

  constructor(private _router: Router,
              private _route: ActivatedRoute,
              private _attendingService: AttendingService,
              private _authService: AuthenticationService,
              private _toastService: ToastService) {
    this.setEmptyEntities();
    this.findOrCreateAttending();
    this.setUpObservers();
  }

  ngOnInit() {
  }

  ngOnDestroy() {
    if (this.creating && !this.wasSaved) {
      this._attendingService.delete(this.attending.id).subscribe();
    }
  }

  findOrCreateAttending() {
    this._route.params.subscribe(params => {
      if (params['id']) {
        this.attendingId = +params['id'];
        this.buildAttending(this.attendingId);
      } else {
        this.creating = true;
        this.createAttending();
        this.canShowAttending = true;
      }
    });
  }

  saveScreening(data) {
    this._attendingService.updateAttending(data);
  }

  saveConfiguration(data) {
    this._attendingService.saveConfiguration(data);
  }

  saveDispatch(data) {
    this._attendingService.saveDispatch(data);
  }

  saveScheduling(data) {
    this._attendingService.saveScheduling(data);
  }

  saveValidation(data) {
    this._attendingService.saveValidation(data);
  }

  updateMonitoring(data) {
    this.updateOnGoingRequestsControl();
    this._toastService.showMessage('Salvando informações do atendimento.');
    this._attendingService.updateAttending(data);
  }

  cancel() {
    if (this.creating) {
      this._attendingService.delete(this.attending.id)
        .subscribe(this.goBack());
    } else {
      this.goBack();
    }
  }

  showStep(step) {
    if (this.attending.orderedSteps && step) {
      return this.attending.currentStep === step || this.stepCanBeShown(step);
    } else {
      this.attending.orderedSteps = [this.SCREENING];
      return this.stepCanBeShown(step);
    }
  }

  stepCanBeShown(step) {
    return this.attending.orderedSteps.includes(step)
              && this.authenticatedUser.stepViews.find(view => view.stepViewType === step);
  }

  setpIsEditable(step) {
    return step && (step.editable || step.editable === undefined);
  }

  assignMonitoringToUser() {
    this.updateOnGoingRequestsControl();
    this._attendingService.assignMonitoringToUser(this.attending.id);
  }

  unassignMonitoringToUser() {
    this.updateOnGoingRequestsControl();
    this._attendingService.unassignMonitoringToUser(this.attending.id);
  }

  updateAttendingInfo(id) {
    if (!this.isLoading) {
      this.updateOnGoingRequestsControl();
      this._attendingService.findAttendingInfo(id);
    }
  }

  convertAttending() {
    this.updateOnGoingRequestsControl();
    this._attendingService.convertAttending(this.attending.id, this.attending.attendingInfo.conversion);
  }

  private updateOnGoingRequestsControl() {
    this.isLoading = true;
    this.totalProcessingRequests++;
  }

  private updateFinishRequestControl() {
    if (this.totalProcessingRequests > 0) {
      this.totalProcessingRequests--;
      if (this.totalProcessingRequests === 0) {
        this.isLoading = false;
      }
    }
  }

  private createAttending() {
    this._attendingService.create();
  }

  private buildAttending(id: number) {
    this._attendingService.build(id);
  }

  private setUpObservers() {
    this._attendingService.save$
      .subscribe((attending) => {
        this.updateFinishRequestControl();
        if (this.attending.currentStep !== this.MONITORING) {
          this.wasSaved = true;
          this.goBack();
        } else {
          this.updateAttendingInfo(attending.attendingInfo.id);
          this.attending = attending;
          this._toastService.showMessage('Atendimento atualizado.');
        }
      });

    this._attendingService.finish$
      .subscribe(() => this.goBack());

    this._attendingService.create$
      .subscribe(attending => {
        this.updateFinishRequestControl();
        this.attending = attending;
        this.setEmptyEntities();
      });

    this._attendingService.build$
      .subscribe(attending => {
        this.updateFinishRequestControl();
        this.attending = attending;
        this.setEmptyEntities();
        this.canShowAttending = true;
      });

    this._attendingService.find$
      .filter(result => result instanceof AttendingInfo)
      .subscribe(attendingInfo => {
        this.updateFinishRequestControl();
        this.attending = { ...this.attending, attendingInfo: attendingInfo };
      });

    this._attendingService.error$
      .subscribe(error => {
        this.updateFinishRequestControl();
        this.showMessage(error.text);
      });
  }

  private setEmptyEntities() {
    this.authenticatedUser = this._authService.getUserFromLocalStorage();
    if (!this.attending.screening) {
      this.attending.screening = new Screening();
    }
    if (!this.attending.configuration) {
      this.attending.configuration = new Configuration();
    }
    if (!this.attending.dispatch) {
      this.attending.dispatch = new Dispatch();
    }
    if (!this.attending.monitoring) {
      this.attending.monitoring = new Monitoring();
    }
    if (!this.attending.equipment) {
      this.attending.equipment = new Equipment();
    }
    if (!this.attending.scheduling) {
      this.attending.scheduling = new Scheduling();
    }
    if (!this.attending.validation) {
      this.attending.validation = new Validation();
      this.attending.validation.gps = false;
      this.attending.validation.speed = false;
      this.attending.validation.rpm = false;
      this.attending.validation.can = false;
    }
    if (!this.attending.screening.providedServices) {
      this.attending.screening.providedServices = [];
    }
  }

  private goBack() {
    this._router.navigate(['attendings', 'list']);
  }

  private showMessage(message) {
    this._toastService.showMessage(message, 4000);
  }
}



// WEBPACK FOOTER //
// /home/jenkins/workspace/production-argos-front/argos/src/app/attending/component/attending/attending.component.ts